import { mergeRefs, useImageLoaded } from '@deltasierra/react-hooks';
import * as React from 'react';
import { useDrag, useDrop } from 'react-dnd';
import styled, { css } from 'styled-components';
import { Loading } from '../Generic/Loaders';
import { MultiItemPickerCloseButton } from './MultiItemPickerCloseButton';

const SelectedCSS = css`
    border: 2px solid ${({ theme }) => theme.palette.primary.main};
    padding: 2px;
`;

const StyledImage = styled.img<{ $isLoading: boolean }>`
    & + & {
        margin-left: 12px;
    }
    opacity: ${({ $isLoading }) => ($isLoading ? '0.75' : '1')};
    cursor: pointer;
`;

const StyledLoading = styled(Loading).attrs({ size: 'small' })`
    position: absolute;
    left: 50%;
    top: 50%;
    transform: translate(-50%, -50%);
`;

const StyledTopRightButton = styled(MultiItemPickerCloseButton)`
    background-color: ${({ theme }) => theme.palette.grey[100]};
    opacity: 0.75;
    position: absolute;
    top: 8px;
    right: 8px;
`;

const ImageContainer = styled.li<{ $isDragHover?: boolean; $selected?: boolean }>`
    padding: 4px;
    position: relative;
    list-style: none;
    ${({ $selected }) => $selected && SelectedCSS}
    ${({ $isDragHover }) =>
        $isDragHover &&
        css`
            border-left: 6px solid ${({ theme }) => theme.palette.primary.main};
        `}
`;

export interface MultiItemPickerImageProps {
    height?: number;
    id: number | string;
    selected?: boolean;
    src?: string;
    title: string;
    width?: number;
    onClick?: () => void;
    onClose?: (id: number | string) => void;
    onDrop?: (from: number | string, to: number | string) => void;
}

export function MultiItemPickerImage({
    height = 125,
    id,
    onClick,
    onClose,
    onDrop,
    selected = false,
    src,
    title,
    width = 125,
}: MultiItemPickerImageProps): JSX.Element {
    const [isLoading, ref] = useImageLoaded(src);
    const [, dragRef] = useDrag({
        collect(monitor) {
            return { isDragging: monitor.isDragging() };
        },
        item: { id },
        type: 'box',
    });

    const [{ canDrop, isOver }, dropRef] = useDrop({
        accept: 'box',
        collect(monitor) {
            return {
                canDrop: monitor.canDrop(),
                isOver: monitor.isOver(),
            };
        },
        drop(item: { id: number | string }) {
            onDrop?.(item.id, id);
        },
    });

    const mergedRefs = mergeRefs([dropRef, dragRef]);

    return (
        <ImageContainer
            $isDragHover={isOver && canDrop}
            $selected={selected}
            data-cy="multi-item-picker-item"
            ref={mergedRefs}
        >
            <StyledImage
                $isLoading={isLoading}
                height={height}
                ref={ref}
                src={src}
                title={title}
                width={width}
                onClick={onClick}
            />
            {onClose && <StyledTopRightButton onClose={() => onClose(id)} />}
            {isLoading && <StyledLoading />}
        </ImageContainer>
    );
}
MultiItemPickerImage.displayName = 'MultiItemPickerImage';
